import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    Paper,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Snackbar,
    IconButton,
    TextField,
    InputAdornment,
    Select,
    MenuItem as SelectItem,
    FormControl,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import { MoreVert, Edit, Delete, ShoppingCart, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DeleteModel from "../../Common/DeleteModel";

import { getAllCategoriesAdmin, deleteCategory } from "../../Apis/CategoriesApi";

const AdminCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [querySearchText, setQuerySearchText] = useState("");
    const [queryStatusFilter, setQueryStatusFilter] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const navigate = useNavigate();



    // Fetch categories from backend
    const fetchCategories = async () => {
        try {
            setLoading(true);
            const query = {
                limit: rowsPerPage,
                page,
                search: querySearchText || undefined,
                status: queryStatusFilter || undefined,
            };
            const data = await getAllCategoriesAdmin(query);
            setCategories(data?.data || []);
            setTotalPages(data?.totalPages || 1);
        } catch (err) {
            console.error("Failed to fetch categories", err);
        } finally {
            setLoading(false);
        }
    };

    const handleMenuOpen = (event, category) => {
        setAnchorEl(event.currentTarget);
        setSelectedCategory(category);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCategory(null);
    };

    const handleSearch = () => {
        setPage(1);
        setQuerySearchText(searchText);
        setQueryStatusFilter(statusFilter);
    };
    const handleEdit = () => {
        navigate(`/getadmincategory/edit/${selectedCategory._id || selectedCategory.id}`);
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        if (!selectedCategory) return;
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteCategory(selectedCategory._id || selectedCategory.id);
            fetchCategories(); // refresh list after deletion
            setSnackbar({ open: true, message: "Category deleted successfully!", severity: "success" });
        } catch (err) {
            console.error("Delete failed", err);
        } finally {
            setDeleteDialogOpen(false);
            setSelectedCategory(null);
            handleMenuClose();

        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedCategory(null);
    };

    const handleProducts = () => {
        if (!selectedCategory) return;
        navigate("/AdminProductsList", { state: { categoryId: selectedCategory._id || selectedCategory.id } });
        handleMenuClose();
    };

    const resetData = () => {
        setSearchText("");
        setStatusFilter("");
        setRowsPerPage(10);
        setPage(1);
        setQuerySearchText("");
        setQueryStatusFilter("");
    };

    useEffect(() => {
        fetchCategories();
    }, [page, rowsPerPage, querySearchText, queryStatusFilter]);

    const normalizeStatus = (val) => val === true || val === "true" || val === 1 || val === "1";
    const getStatusLabel = (cat) => (normalizeStatus(cat.status) ? "Active" : "Inactive");

    return (
        <Box sx={{ padding: 3, width: "95%" }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">Categories</Typography>
                <Button variant="contained" onClick={() => navigate("/getadmincategory/add")}>
                    + Add Category
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
                <TextField
                    placeholder="Search categories"
                    size="small"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                        displayEmpty
                    >
                        <SelectItem value="">All Status</SelectItem>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                    </Select>
                </FormControl>
                <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
                    <Button
                        sx={{ width: "100px" }}
                        variant="contained"
                        onClick={() => { handleSearch() }}
                    >
                        Search
                    </Button>
                    <Button
                        sx={{ width: "120px" }}
                        variant="outlined"
                        onClick={() => {
                            resetData()
                        }}
                    >
                        Reset
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            {loading ? (
                <Box sx={{ textAlign: "center", py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Order</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categories.length > 0 ? (
                                categories.map((cat) => (
                                    <TableRow key={cat._id || cat.id}>
                                        <TableCell>
                                            <img
                                                src={cat.imageUrl || "/placeholder.png"}
                                                alt={cat.name}
                                                width={60}
                                                height={60}
                                                style={{ objectFit: "cover", borderRadius: 4 }}
                                            />
                                        </TableCell>
                                        <TableCell>{cat.name}</TableCell>
                                        <TableCell>{cat.order}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(cat)}
                                                color={normalizeStatus(cat.status) ? "success" : "error"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={(e) => handleMenuOpen(e, cat)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No Categories found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value));
                            setPage(1);
                        }}
                    >
                        {[5, 10, 20, 50].map((num) => (
                            <SelectItem key={num} value={num}>{num} Rows</SelectItem>
                        ))}
                    </Select>
                </FormControl>

                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, val) => setPage(val)}
                />
            </Box>

            {/* Action Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDeleteClick}>
                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: "error" }}>Delete</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleProducts}>
                    <ListItemIcon><ShoppingCart fontSize="small" /></ListItemIcon>
                    <ListItemText>Products</ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <DeleteModel
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                entityName="category"
                entityLabel={selectedCategory?.name}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            />
        </Box>
    );
};

export default AdminCategories;
